// S4 (v0.15.0) — Active sessions list with revoke.
import { LaptopIcon, RefreshCwIcon, SmartphoneIcon } from "lucide-react";

import { useRevokeSession, useSessions } from "@/api/s4";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { formatTimestamp } from "@/utils/datetime";

function parseUserAgent(ua: string | null): { name: string; mobile: boolean } {
  if (!ua) return { name: "Unknown device", mobile: false };
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  let browser = "Unknown browser";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  let os = "Unknown OS";
  if (/Mac OS X/.test(ua)) os = "Mac";
  else if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return { name: `${browser} on ${os}`, mobile };
}

export default function SessionManagerPage() {
  // `data: sessions = []` DEFAULTS A FAILURE TO EMPTY, and the empty state on
  // this page reads "No active sessions." — a positive security claim ("nobody
  // is signed in anywhere") made on the strength of a request that never
  // landed. Same shape as APITokens, on the surface where a user comes
  // specifically to check whether someone else is logged in as them.
  const {
    data: sessions = [],
    refetch,
    isFetching,
    isLoading,
    isError,
    error,
  } = useSessions();
  const revoke = useRevokeSession();

  async function revokeAllOthers() {
    if (!confirm("Revoke every session other than this one?")) return;
    const others = sessions.filter((s) => !s.is_current);
    for (const s of others) await revoke.mutateAsync(s.id);
    await refetch();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Active Sessions
          </h1>
          <p className="text-sm text-muted-foreground">
            Every device currently signed in to your account.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCwIcon className={`mr-1 h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={revokeAllOthers}>
            Revoke All Other Sessions
          </Button>
        </div>
      </div>

      {/* isEmpty is handled here rather than by the in-table row so "you have
          no other sessions" and "we could not check" cannot render alike. */}
      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="your active sessions"
        onRetry={() => void refetch()}
        isEmpty={sessions.length === 0}
        emptyMessage="No active sessions."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Device</th>
                <th className="px-3 py-2 text-left">IP</th>
                <th className="px-3 py-2 text-left">Last Active</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                  const { name, mobile } = parseUserAgent(s.user_agent);
                  return (
                    <tr key={s.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-2">
                          {mobile ? (
                            <SmartphoneIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <LaptopIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          {name}
                          {s.is_current && (
                            <Badge variant="online">Current</Badge>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {s.ip_address ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {formatTimestamp(s.last_active_at, "relative")}
                      </td>
                      <td className="px-3 py-2">
                        {formatTimestamp(s.created_at, "datetime")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={s.is_current}
                          onClick={() => {
                            if (
                              confirm(
                                "Revoke this session? That device will be logged out.",
                              )
                            )
                              revoke.mutate(s.id);
                          }}
                        >
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
